import {
  Accordion,
  Badge,
  CheckboxCard,
  CheckboxCardGroup,
  Grid,
  TextArea,
} from '@velocity/ui'
import * as React from 'react'
import { useFormState } from 'react-final-form'

import { useContent } from '@ngb-frontend/shared/context'
import { FormField, FormFieldGroupWrapper } from '@ngb-frontend/shared/ui'
import { string } from '@ngb-frontend/shared/utils'

import { useMalfunctionGroupsFieldStyles } from './MalfunctionGroupsField.styled'

import type {
  FormFieldWrapper,
  MalfunctionFormValues,
  MalfunctionGroup,
  MalfunctionSets,
} from '@ngb-frontend/shared/types'
import type { ReactElement } from 'react'

const OTHER_GROUP: MalfunctionGroup = 'otherIssues'
const FREE_TEXT_CODE = 'Other'

interface MalfunctionGroupsFieldProps
  extends Pick<FormFieldWrapper, 'error' | 'dirty'> {
  malfunctions: MalfunctionSets
  initialValues?: MalfunctionFormValues
}

export const MalfunctionGroupsField: React.FC<MalfunctionGroupsFieldProps> = ({
  malfunctions,
  initialValues,
  dirty,
  error,
}) => {
  const classes = useMalfunctionGroupsFieldStyles()
  const c = useContent()
  const { values } = useFormState<MalfunctionFormValues>({
    subscription: { values: true },
  })

  const groupLabel = React.useCallback(
    (group: MalfunctionGroup) =>
      c(`steps.malfunction.group.${group === OTHER_GROUP ? 'other' : group}`),
    [c],
  )

  const groups = (Object.keys(malfunctions) as MalfunctionGroup[]).sort(
    (a, b) => {
      if (a === OTHER_GROUP) return 1
      if (b === OTHER_GROUP) return -1

      return groupLabel(a).localeCompare(groupLabel(b))
    },
  )

  const selectedCount = (group: MalfunctionGroup) =>
    (values.malfunctions?.[group]?.filter(
      (code) => code && code !== FREE_TEXT_CODE,
    ).length || 0) +
    (group === OTHER_GROUP && string(values.malfunctionDescription).length
      ? 1
      : 0)

  return (
    <FormFieldGroupWrapper dirty={dirty} error={error}>
      <Grid
        data-e2e-component="malfunctions"
        gutter={{
          x: 'var(--vel-layout-spacing-06)',
          y: 'var(--vel-layout-spacing-02)',
        }}
      >
        {
          groups.map((group) => {
            const codes = malfunctions[group] || []
            const hasFreeText =
              group === OTHER_GROUP && codes.includes(FREE_TEXT_CODE)
            const options = codes
              .filter((code) => !(hasFreeText && code === FREE_TEXT_CODE))
              .map((code) => ({
                title: c(`steps.malfunction.item.${code}`),
                value: code,
              }))
              .sort((a, b) => a.title.localeCompare(b.title))
            const count = selectedCount(group)
            const initialCodes = initialValues?.malfunctions?.[group]

            return (
              <Grid.Item LG={6} key={group}>
                <Accordion
                  initialExpanded={
                    !!initialCodes?.filter(Boolean).length ||
                    (hasFreeText &&
                      !!initialValues?.malfunctionDescription?.length)
                  }
                >
                  <Accordion.Header>
                    <span className={classes.header}>
                      {groupLabel(group)}
                      {count > 0 && (
                        <Badge
                          kind="neutral"
                          data-e2e-component={`malfunctions-${group}-count`}
                        >
                          {String(count)}
                        </Badge>
                      )}
                    </span>
                  </Accordion.Header>
                  <Accordion.Content>
                    {hasFreeText && (
                      <div className={classes.freeText}>
                        <FormField<string>
                          id="malfunctions-other-group"
                          name="malfunctionDescription"
                          label={c('steps.malfunction.item.Other')}
                          hideLabel
                          required={false}
                          hideOptionalLabel={true}
                        >
                          {({ input }) => (
                            <TextArea
                              {...input}
                              inputProps={{
                                'data-e2e-component':
                                  'malfunctions-other-input',
                              }}
                              maxLength={1000}
                            />
                          )}
                        </FormField>
                      </div>
                    )}
                    <FormField<string[]>
                      id={`malfunctions-${group}-group`}
                      name={`malfunctions.${group}`}
                      label={groupLabel(group)}
                      hideLabel
                      group={true}
                      required={false}
                      hideOptionalLabel={true}
                    >
                      {({ input }) => {
                        // eslint-disable-next-line @typescript-eslint/no-unused-vars
                        const { onFocus, onBlur, onChange, ...restInput } =
                          input

                        return (
                          <CheckboxCardGroup
                            {...restInput}
                            value={input.value || []}
                            className={classes.checkboxCardGroup}
                            onChange={(e) => onChange(e.target.value)}
                            data-e2e-component={`malfunctions-${group}-group`}
                          >
                            {options.map((option) => (
                              <CheckboxCard {...option} key={option.value} />
                            ))}
                          </CheckboxCardGroup>
                        )
                      }}
                    </FormField>
                  </Accordion.Content>
                </Accordion>
              </Grid.Item>
            )
          }) as unknown as ReactElement
        }
      </Grid>
    </FormFieldGroupWrapper>
  )
}
